import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import * as Sentry from '@sentry/vue';
import Userback, {
  type UserbackOptions,
  type UserbackWidget,
  type UserbackFeedbackType,
  type UserbackDestinationType,
} from '@userback/widget';

/**
 * Owns the Userback feedback widget: its lifecycle, the loaded instance, and
 * the actions components use to drive it (e.g. a custom "Give feedback" button).
 *
 * The widget is held in a `shallowRef` — it's an external SDK object, so we
 * don't want Vue deep-proxying its internals. Components should call the action
 * wrappers below rather than touching `widget` directly.
 */
export const useUserbackStore = defineStore('userback', () => {
  const widget = shallowRef<UserbackWidget | null>(null);

  /**
   * Load the widget. No-op when the token is empty (e.g. local dev) or when it
   * has already loaded. A load failure is reported to Sentry and swallowed so
   * it never breaks the app. Called lazily behind the feedback gate (see
   * `NavFeedbackLink`), so a torn-down widget re-initialises on the next use.
   */
  const init = async (token: string, options?: UserbackOptions): Promise<void> => {
    if (!token || widget.value) return;

    try {
      widget.value = await Userback(token, options);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  /** Open the feedback popup. Wire this to your custom trigger button. */
  const open = (
    feedbackType?: UserbackFeedbackType,
    destination?: UserbackDestinationType,
  ): void => {
    widget.value?.open(feedbackType, destination);
  };

  const close = (): void => {
    widget.value?.close();
  };

  /**
   * Re-attribute the loaded widget to the current user, e.g. after a login that
   * didn't reload the page. No-op when the widget isn't loaded (never opened, or
   * torn down by logout) — it re-initialises with the current user on next use.
   */
  const identify = (userId: string, userInfo: Record<string, unknown>): void => {
    widget.value?.identify(userId, userInfo);
  };

  /**
   * Setup stores have no built-in `$reset` — Pinia's default throws when called.
   * The app's reset helpers (`clearAllPiniaStores` on logout) iterate every
   * store and call `$reset()`, so we provide one that tears the widget down and
   * clears the reference. (Org switches deliberately skip this store — see
   * `clearStoresForOrganisationSwitch`'s exclude list — so the widget survives
   * an org switch.)
   */
  const $reset = (): void => {
    widget.value?.destroy();
    widget.value = null;
  };

  return { widget, init, open, close, identify, $reset };
});
